import { useState, useEffect } from "react";
import { useAuth } from '../../contexts/AuthContext';
import { auth, signInWithEmailAndPassword, sendPasswordResetEmail } from "../../firebase";
import { useNavigate } from "react-router";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ErrorDisplay from '../../components/ErrorDisplay';
import * as theme from '../../theme';

export default function SignIn() {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser]);

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const clearError = () => setError('');
  const clearInfo = () => setInfo('');

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    if (!password) {
      setError('Please enter a password.');
      setLoading(false);
      return;
    }

    try {
      const normalizedEmail = email.toLowerCase().trim();
      await signInWithEmailAndPassword(auth, normalizedEmail, password);
      navigate('/dashboard');
    } catch (err) {
      console.error('Sign-in error:', err.message);
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    }

    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setError('');
    setInfo('');

    if (!isValidEmail(resetEmail)) {
      setError('Please enter a valid email address.');
      setResetLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setInfo('Password reset email sent. Please check your inbox.');
      setShowReset(false);
    } catch (err) {
      console.error('Reset password error:', err.message);
      setError(err.message || 'Failed to send reset email. Please try again.');
    }

    setResetLoading(false);
  };

  return (
    <div style={{ ...theme.Container.basicWide, marginTop: 20 }}>
      <div style={theme.Heading}>Sign In</div>
      <div style={theme.SubHeadingText}>
        Enter your email and password to login to your account.
      </div>
      <div style={{ marginBottom: 30 }}>
        <div style={{ marginBottom: 20 }}>
          <ErrorDisplay
            message={error || info}
            type={error ? 'error' : 'info'}
            clearMessage={error ? clearError : clearInfo}
          />
        </div>
        {!showReset ? (
          <Box
            component="form"
            onSubmit={handleSignIn}
            sx={{ ...theme.FullForm.form, gap: 4, alignItems: 'center' }}
          >
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="filled"
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="filled"
              required
              fullWidth
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ width: 200 }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
            <Button
              onClick={() => setShowReset(true)}
              variant="outlined"
              color="secondary"
              disabled={loading}
              sx={{ width: 200 }}
            >
              Forgot Password?
            </Button>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleResetPassword}
            sx={{ ...theme.FullForm.form, gap: 4, alignItems: 'center' }}
          >
            <TextField
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              variant="filled"
              required
              fullWidth
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={resetLoading}
              sx={{ width: 200 }}
            >
              {resetLoading ? 'Sending...' : 'Send Reset Email'}
            </Button>
            <Button
              onClick={() => setShowReset(false)}
              variant="outlined"
              color="secondary"
              disabled={loading}
              sx={{ width: 200 }}
            >
              Return to Sign In
            </Button>
          </Box>
        )}
      </div>
    </div>
  );
}